<template>
    <PageLoading :loading="loading">
        <ErrorPage v-if="hasError" :title="errorTitle" :message="errorMessage" />
        <div v-else class="bind-student-page">

            <div class="school-info">
                <span class="school-name">{{ schoolInfo.school_name }}</span>
                <span class="class-name">{{ schoolInfo.grade_name }}{{ schoolInfo.name }}</span>
                <div class="separator"></div>
                <span class="semester">{{ schoolInfo.term_name }}</span>
            </div>

            <div class="school-info-section">
                既可以订奶，又可以到机器上刷脸购买。家长绑定学生后，点下方按钮，去公众号充值或订奶。
            </div>

            <div class="search-section">
                <van-field v-model="searchKeyword" placeholder="请输入学生姓名" clearable class="search-input"
                    @keyup.enter="onSearch" @clear="onSearch">
                    <template #left-icon>
                        <van-icon name="search" @click="onSearch" />
                    </template>
                </van-field>
            </div>

            <div class="students-grid">
                <div class="students-list">
                    <div :class="student.parent_ids.length > 0 ? 'student-card bound' : 'student-card available'"
                        v-for="student in schoolInfo.student_list" :key="student.id"
                        @click="student.parent_ids.length === 0 ? showConfirmModal(student) : null">
                        <div class="student-name">{{ student.name }}</div>
                        <div class="bound-status" v-if="student.parent_ids.length > 0">已绑定</div>
                        <div class="selection-indicator" v-if="selectedStudents.includes(student.id)">
                            <van-icon name="success" />
                        </div>
                    </div>
                </div>
            </div>

            <div class="add-student-section">
                <van-button type="default" class="add-student-btn" @click="addStudent" icon="plus">
                    添加学生
                </van-button>
            </div>

            <div class="confirm-section">
                <van-button type="primary" class="confirm-btn" @click="goToWechat">
                    点击去公众号订奶或充值
                </van-button>
            </div>

            <!-- 添加学生弹框 -->
            <van-popup v-model:show="showAddStudentModal" position="center" round :style="{ width: '90%' }">
                <div class="add-student-popup">
                    <div class="popup-header">
                        <div class="popup-title">添加学生</div>
                        <van-icon name="cross" @click="showAddStudentModal = false" class="close-icon" />
                    </div>
                    <div class="popup-content">
                        <van-field v-model="newStudentName" placeholder="请输入学生姓名" clearable class="name-input" />
                    </div>
                    <div class="popup-footer">
                        <van-button type="default" @click="showAddStudentModal = false"
                            class="cancel-btn">取消</van-button>
                        <van-button type="primary" @click="handleAddStudent" :disabled="!newStudentName.trim()"
                            class="confirm-add-btn">确定</van-button>
                    </div>
                </div>
            </van-popup>

            <!-- 扫码关注公众号弹框 -->
            <van-popup v-model:show="showWechatModal" position="center" round :style="{ width: '90%' }">
                <div class="wechat-popup">
                    <div class="popup-header">
                        <div class="popup-title">扫码关注公众号</div>
                        <van-icon name="cross" @click="showWechatModal = false" class="close-icon" />
                    </div>
                    <div class="popup-content" v-if="qrCode">
                        <div class="qr-code-container">
                            <img :src="qrCode" alt="公众号二维码" class="qr-code" />
                        </div>
                        <div class="qr-tip">长按图片识别二维码</div>
                    </div>
                </div>
            </van-popup>

            <LoadingOverlay :show="isLoading" />
        </div>
    </PageLoading>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { setWxTitle } from '@/utils/wx'
import { Toast, Dialog } from 'vant'
import PageLoading from '@/components/PageLoading.vue'
import ErrorPage from '@/components/ErrorPage.vue'
import { post } from '@/utils/request'
import { WECHAT_APPID } from '../../../config/env'
import LoadingOverlay from '@/components/LoadingOverlay.vue'

onMounted(() => {
    setWxTitle('绑定学生')
    loadStudents()
})

// 加载状态
const loading = ref(true)
const hasError = ref(false)
const errorTitle = ref('')
const errorMessage = ref('')
const isLoading = ref(false)

const handleError = (title, message) => {
    hasError.value = true
    errorTitle.value = title
    errorMessage.value = message
    loading.value = false
}
const classId = ref(0)
// 搜索关键词
const searchKeyword = ref('')

// 学校信息
const schoolInfo = ref({})

// 学生数据
const selectedStudents = ref([])
const showAddStudentModal = ref(false)
const newStudentName = ref('')
const selectedStudentForBinding = ref(null)
const showWechatModal = ref(false)
// 二维码
const qrCode = ref('')

// 初始化微信授权
const initWechatAuth = () => {
    // 获取当前url中的code参数
    const urlParams = new URLSearchParams(window.location.search)
    const code = urlParams.get('code')

    // 判断用户取消授权
    const error = urlParams.get('error')
    const errorDesc = urlParams.get('error_description')
    if (error) {
        console.log('用户取消授权:', errorDesc)
        handleError('获取用户信息失败', errorDesc)
        return
    }

    if (!code) {
        // 如果没有code，重定向到微信授权页面
        const redirectUri = encodeURIComponent(window.location.href)
        const appId = WECHAT_APPID
        window.location.href = `https://open.weixin.qq.com/connect/oauth2/authorize?appid=${appId}&redirect_uri=${redirectUri}&response_type=code&scope=snsapi_userinfo&state=STATE#wechat_redirect`
    } else {
        // 有code则获取用户信息
        getUserInfo({
            code: code,
            app_id: WECHAT_APPID,
            page_type: '首页'
        })
    }
}

// 获取用户信息
const getUserInfo = async (data) => {
    try {
        const res = await post('/api/index/info', data)

        if (res.result && res.result.state === 'ok') {
            const userInfo = res.result.partner_info || {}
            const accountInfo = res.result.account_info || {}
            // 存储用户ID到本地存储
            if (userInfo.id) {
                localStorage.setItem('userId', userInfo.id)
            }
            if (accountInfo.company_ids) {
                localStorage.setItem('company_ids', JSON.stringify(accountInfo.company_ids))
            }
            if (accountInfo.user_default_image) {
                localStorage.setItem('user_default_image', accountInfo.user_default_image)
            }
            loadStudents()
        } else {
            handleError('获取用户信息失败', res.result?.message || '获取用户信息失败')
        }
    } catch (error) {
        console.error('获取用户信息失败:', error)
        handleError('获取用户信息失败', '获取用户信息失败，请重试')
    } finally {
        loading.value = false
    }
}

// 加载学生数据
const loadStudents = async () => {
    try {
        const urlParams = new URLSearchParams(window.location.search)
        classId.value = parseInt(urlParams.get('class_id'))
        // 检查是否已有用户ID
        if (localStorage.getItem('userId')) {
            const params = {
                class_id: classId.value,
                app_id: WECHAT_APPID,
            }

            if (searchKeyword.value) {
                params.student_name = searchKeyword.value
            }

            const res = await post('/api/select/student/by/class', params)

            if (res.result && res.result.state === 'ok') {
                schoolInfo.value = res.result.class_info || {}
                qrCode.value = res.result.account_info?.qr_code_image || ''
                loading.value = false
            } else {
                handleError('加载失败', res.result?.message || '接口异常')
                loading.value = false
            }
        } else {
            initWechatAuth()
        }
    } catch (err) {
        console.error('加载学生数据失败:', err)
        handleError('加载失败', '获取学生列表失败')
        loading.value = false
    }
}

const onSearch = () => {
    console.log('onSearch', searchKeyword.value)
    loadStudents()
}

// 显示确认绑定弹框
const showConfirmModal = (student) => {
    selectedStudentForBinding.value = student
    Dialog.confirm({
        title: '确认绑定',
        message: `确认与${student.name}的绑定关系？`,
        confirmButtonText: '确认绑定',
        cancelButtonText: '取消',
    }).then(() => {
        // 点击确认绑定
        confirmStudentBinding()
    }).catch(() => {
        // 点击取消
        selectedStudentForBinding.value = null
    })
}

// 确认绑定学生
const confirmStudentBinding = async () => {
    if (!selectedStudentForBinding.value) {
        return
    }

    try {
        isLoading.value = true
        const student = selectedStudentForBinding.value
        const res = await post('/api/add/child/student', {
            partner_id: parseInt(localStorage.getItem('userId')),
            student_id: student.id,
            name: student.name,
        })

        if (res.result && res.result.state === 'ok') {
            Toast.success('绑定成功')
            selectedStudentForBinding.value = null
            loadStudents()
        } else {
            Dialog.alert({
                title: '绑定失败',
                message: res.result?.message || '绑定失败'
            })
        }
    } catch (err) {
        console.error('绑定学生失败:', err)
        Dialog.alert({
            title: '绑定失败',
            message: '绑定失败，请重试'
        })
    } finally {
        isLoading.value = false
    }
}

// 添加学生
const addStudent = () => {
    showAddStudentModal.value = true
    newStudentName.value = '' // 清空输入框
}

// 处理添加学生
const handleAddStudent = async () => {
    if (!newStudentName.value.trim()) {
        Toast('学生姓名不能为空')
        return
    }

    try {
        isLoading.value = true
        const res = await post('/api/add/child/student', {
            class_id: classId.value,
            name: newStudentName.value.trim(),
            partner_id: parseInt(localStorage.getItem('userId')),
            grade_id: schoolInfo.value.grade_id,
            school_id: schoolInfo.value.school_id,
        })

        if (res.result && res.result.state === 'ok') {
            Toast.success('学生添加成功')
            showAddStudentModal.value = false
            newStudentName.value = ''
            loadStudents()
        } else {
            Toast.fail(res.result?.message || '添加失败')
        }
    } catch (err) {
        console.error('添加学生失败:', err)
        Toast.fail('添加学生失败，请重试')
    } finally {
        isLoading.value = false
    }
}

// 去公众号订奶或充值
const goToWechat = () => {
    showWechatModal.value = true
}

</script>

<style scoped>
.bind-student-page {
    background: #fff;
    min-height: calc(100vh - 90px);
    padding-bottom: 90px;
}

.school-info {
    padding: 14px;
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 14px;
    flex-wrap: wrap;
}

.school-info-section {
    margin: 0 14px 14px;
    padding: 14px;
    font-size: 14px;
    color: #666;
    border: 1px solid #f0f0f0;
    border-radius: 8px;
}

.school-name {
    font-size: 18px;
    font-weight: 600;
    color: #333;
}

.class-name,
.semester {
    color: #999;
    font-weight: 400;
}

.separator {
    width: 1px;
    height: 12px;
    background: #6B7280;
}

.search-section {
    padding: 0 14px;
}

.search-input {
    border-radius: 8px;
    background: #f7f8fa;
}

.search-input :deep(.van-field__control) {
    background: transparent;
}

.students-list {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 10px;
    padding: 14px;
}

.student-card {
    height: 50px;
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    position: relative;
    cursor: pointer;
    transition: all 0.2s;
}

.student-card.bound {
    background: #f7f8fa;
    color: #999;
    cursor: not-allowed;
    border: 1px solid #e5e5e5;
    opacity: 0.7;
}

.student-card.available {
    background: #fff;
    border: 1px solid #e5e5e5;
    color: #333;
}

.student-card.selected {
    background: var(--van-primary-color);
    color: #fff;
    border-color: var(--van-primary-color);
}

.student-name {
    font-size: 14px;
    font-weight: 500;
}

.bound-status {
    position: absolute;
    top: 4px;
    right: 8px;
    color: #999;
    font-size: 10px;
    font-weight: 400;
    z-index: 1;
}

.selection-indicator {
    position: absolute;
    top: 4px;
    right: 4px;
    width: 16px;
    height: 16px;
    background: #fff;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
}

.selection-indicator .van-icon {
    font-size: 12px;
    color: var(--van-primary-color);
}

.add-student-section {
    padding: 0 14px 14px;
    display: flex;
    justify-content: flex-start;
}

.add-student-btn {
    border: 1px dashed #ccc;
    color: #666;
    background: #fff;
    border-radius: 8px;
    height: 48px;
    padding: 0 16px;
}

.confirm-section {
    position: fixed;
    bottom: 0;
    left: 0;
    right: 0;
    padding: 14px 14px 20px 14px;
    background: #fff;
    border-top: 1px solid #f0f0f0;
}

.confirm-btn {
    width: 100%;
    height: 48px;
    border-radius: 8px;
    font-size: 16px;
    font-weight: 500;
}

.add-student-popup {
    padding: 20px;
    display: flex;
    flex-direction: column;
    align-items: center;
}

.popup-header {
    width: 100%;
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 20px;
}

.popup-title {
    font-size: 16px;
    font-weight: 500;
    color: #333;
}

.close-icon {
    font-size: 20px;
    color: #999;
}

.popup-content {
    width: 100%;
    margin-bottom: 20px;
}

.name-input {
    border-radius: 8px;
    background: #f7f8fa;
}

.popup-footer {
    width: 100%;
    display: flex;
    gap: 12px;
}

.cancel-btn {
    background: #f5f5f5;
    border: none;
    color: #4B5563;
    border-radius: 8px;
    height: 38px;
    flex: 1;
}

.confirm-add-btn {
    border-radius: 8px;
    height: 38px;
    flex: 1;
}

.wechat-popup {
    padding: 20px;
    display: flex;
    flex-direction: column;
    align-items: center;
}

.wechat-popup .popup-header {
    width: 100%;
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 20px;
}

.wechat-popup .popup-title {
    font-size: 16px;
    font-weight: 500;
    color: #333;
}

.wechat-popup .close-icon {
    font-size: 20px;
    color: #999;
}

.wechat-popup .popup-content {
    width: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
}

.qr-code-container {
    margin-bottom: 16px;
}

.qr-code {
    width: 200px;
    height: 200px;
    border-radius: 8px;
}

.qr-tip {
    font-size: 14px;
    color: #666;
    text-align: center;
}
</style>